/**
 * 图片存储
 *
 * 新上传走远程（POST /api/media/uploads → MinIO images/{uid}/uploads/），
 * 跟账号走、跨设备可见；上传期间用本地 blob URL 乐观预览，完成后换网关地址。
 *
 * 旧的 IndexedDB 缓存层（blob + storageKey 自愈）保留，仅服务存量节点：
 * 页面刷新后 blob URL 失效，由 resolveImageUrl() 重新解析。
 *
 * DB: canvas_image_cache
 *   Store: images (keyPath: storageKey)
 *   Record: { storageKey: string, blob: Blob, createdAt: number }
 */

import { boardActions } from './state/boardStore';
import { toPersistableMediaUrl } from './utils/persistableMediaUrl';
import { platformFetch } from '../../platform/http';
import toast from '../../components/Toast';

// ---------------- 常量 ----------------

const DB_NAME = 'canvas_image_cache';
const DB_VERSION = 1;
const STORE_NAME = 'images';
const STORAGE_KEY_PREFIX = 'img:';

// blob URL 缓存（内存中，页面刷新后失效）
const blobUrlCache = new Map<string, string>();

// ---------------- DB 初始化 ----------------

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'storageKey' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      dbPromise = null;
      reject(request.error);
    };
  });

  return dbPromise;
}

// ---------------- 工具函数 ----------------

/** 判断一个字符串是否是 storageKey（而非普通 URL） */
export function isStorageKey(value: string): boolean {
  return value.startsWith(STORAGE_KEY_PREFIX);
}

// ---------------- 核心 API：远程上传（跟账号走） ----------------

export interface RemoteUploadHandle {
  /** 本地即时预览（blob: 会话链接；上传完成后由绑定方换掉） */
  previewUrl: string;
  /** 远程上传完成后 resolve 持久网关地址；失败 reject */
  settled: Promise<string>;
}

function blobToDataUrl(blob: Blob): Promise<string | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(blob);
  });
}

async function uploadToRemote(dataUrl: string): Promise<string> {
  const response = await platformFetch('/media/uploads', {
    method: 'POST',
    body: JSON.stringify({ image: dataUrl }),
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || '图片上传失败');
  }
  const body = await response.json();
  const url: string | undefined = body?.url ?? body?.data?.url;
  if (!url) throw new Error('图片上传失败');
  return url;
}

/**
 * 上传图片到远程用户目录（images/{uid}/uploads/），立即返回本地预览与远程结果句柄。
 * projectId 仅为旧签名兼容保留：远程 key 按用户命名空间组织，不再按项目隔离。
 */
export function uploadImage(blob: Blob, _projectId?: string): RemoteUploadHandle {
  const previewUrl = URL.createObjectURL(blob);
  const settled = (async () => {
    const dataUrl = await blobToDataUrl(blob);
    if (!dataUrl) throw new Error('本地图片读取失败');
    return uploadToRemote(dataUrl);
  })();
  return { previewUrl, settled };
}

/**
 * 乐观绑定：节点先渲染本地预览，上传完成后把 imageUrl 换成持久网关地址。
 * 失败时保留会话内预览并提示（刷新后该图丢失，可重新上传）。
 */
export function bindUploadToNode(nodeId: string, handle: RemoteUploadHandle): void {
  void handle.settled.then(
    (remoteUrl) => {
      const persistable = toPersistableMediaUrl(remoteUrl) ?? remoteUrl;
      boardActions.updateNodeData(nodeId, { imageUrl: persistable, _imageStorageKey: null });
      URL.revokeObjectURL(handle.previewUrl);
    },
    (err) => {
      console.error('[canvas] 远程图片上传失败:', err);
      URL.revokeObjectURL(handle.previewUrl);
      toast.error('图片上传失败，该图仅当前会话可见');
    }
  );
}

/**
 * 将 File/Blob 上传到远程（便捷入口）。
 * 用于拖拽上传和文件选择场景。
 */
export function uploadFile(file: File | Blob, projectId?: string): RemoteUploadHandle {
  return uploadImage(file instanceof Blob ? file : new Blob([file]), projectId);
}

/**
 * 把会话内的 blob: 图片引用远程化（聚合进图片组等"吸收"操作前使用）：
 * blob → data URL → 远程上传 → 持久网关地址。非 blob 原样返回；
 * blob 已失效或上传失败返回 null（调用方应阻止本次吸收，
 * 否则组槽位会存进不可恢复的死链）。
 */
export async function remoteizeBlobUrl(url: string): Promise<string | null> {
  if (!url.startsWith('blob:')) return url;
  const dataUrl = await blobUrlToDataUrl(url);
  if (!dataUrl) return null;
  try {
    const remote = await uploadToRemote(dataUrl);
    return toPersistableMediaUrl(remote) ?? remote;
  } catch {
    return null;
  }
}

// ---------------- 蒙版专用：IndexedDB 旧链路 ----------------

/**
 * 蒙版（inpaint 手绘产物）仍走 IndexedDB + blob 引用：
 * 蒙版是易重生的临时数据，且其后端提交链路按 data URL 设计，
 * 不随本次「本地上传跟账号走」改造切换。
 */
export async function uploadMaskToIndexedDB(blob: Blob, projectId?: string): Promise<{ storageKey: string; blobUrl: string }> {
  const db = await openDB();
  const storageKey = `${STORAGE_KEY_PREFIX}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ storageKey, blob, projectId: projectId ?? null, createdAt: Date.now() });
    tx.oncomplete = () => {
      const blobUrl = URL.createObjectURL(blob);
      blobUrlCache.set(storageKey, blobUrl);
      resolve({ storageKey, blobUrl });
    };
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * 从 IndexedDB 读取图片并创建 blob URL（存量节点自愈用）。
 * 如果内存缓存中已有 blobUrl，直接返回（避免重复创建）。
 */
export async function resolveImageUrl(storageKey: string): Promise<string | null> {
  // 先检查内存缓存
  const cached = blobUrlCache.get(storageKey);
  if (cached) return cached;

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(storageKey);

      request.onsuccess = () => {
        const record = request.result;
        if (!record) {
          resolve(null);
          return;
        }
        const blobUrl = URL.createObjectURL(record.blob);
        blobUrlCache.set(storageKey, blobUrl);
        resolve(blobUrl);
      };
      request.onerror = () => reject(request.error);
    });
  } catch {
    // IndexedDB 不可用时降级
    return null;
  }
}

/**
 * 从 IndexedDB 删除指定图片。
 */
export async function deleteImage(storageKey: string): Promise<void> {
  // 释放 blob URL
  const cached = blobUrlCache.get(storageKey);
  if (cached) {
    URL.revokeObjectURL(cached);
    blobUrlCache.delete(storageKey);
  }

  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(storageKey);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IndexedDB 不可用时静默失败
  }
}

/**
 * GC：清理未被引用的图片。
 *
 * IndexedDB 是所有画布项目共享的，因此必须按项目隔离删除：
 *  - 只删除「归属当前项目」且「未被当前项目节点引用」的记录；
 *  - 归属其他项目 / 无归属标记（历史遗留）的记录一律保留，
 *    宁可多占本地空间，也不能误删其他项目仍在引用的图片。
 *
 * @param usedKeys - 当前项目中所有节点引用的 storageKey 集合
 * @param projectId - 当前项目 ID；为空时不执行任何删除
 */
export async function cleanupUnusedImages(usedKeys: Set<string>, projectId?: string): Promise<number> {
  if (!projectId) return 0;
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const records = request.result as Array<{ storageKey: string; projectId?: string | null }>;
        let deletedCount = 0;

        for (const record of records) {
          const key = record.storageKey;
          if (record.projectId !== projectId) continue;
          if (!usedKeys.has(key)) {
            store.delete(key);
            // 释放 blob URL
            const cached = blobUrlCache.get(key);
            if (cached) {
              URL.revokeObjectURL(cached);
              blobUrlCache.delete(key);
            }
            deletedCount++;
          }
        }

        tx.oncomplete = () => resolve(deletedCount);
      };
      request.onerror = () => reject(request.error);
    });
  } catch {
    return 0;
  }
}

/**
 * 收集画布节点数据中所有 storageKey 引用。
 * 遍历所有节点的 data 字段，找出值以 STORAGE_KEY_PREFIX 开头的。
 */
export function collectStorageKeys(
  nodes: Array<{ data: Record<string, unknown> }>
): Set<string> {
  const keys = new Set<string>();

  for (const node of nodes) {
    for (const value of Object.values(node.data)) {
      if (typeof value === 'string' && isStorageKey(value)) {
        keys.add(value);
      }
      // imageUrls 数组的情况
      if (Array.isArray(value)) {
        for (const item of value) {
          if (typeof item === 'string' && isStorageKey(item)) {
            keys.add(item);
          }
        }
      }
    }
  }

  return keys;
}

// ---------------- 生成 API 参考图解析 ----------------

/**
 * 将 blob: URL 转换为 data URL。
 *
 * 后端无法访问浏览器内存中的 blob URL，本地上传的图片在提交生成 API 前
 * 必须转为 base64 data URL（后端 saveReferenceImagesAsFiles 支持 data: 格式）。
 * 解析失败返回 null，由调用方过滤。
 */
export async function blobUrlToDataUrl(blobUrl: string): Promise<string | null> {
  try {
    // 优先通过 blobUrlCache 反查 storageKey，从 IndexedDB 读原始 blob
    let storageKey: string | null = null;
    for (const [key, url] of blobUrlCache) {
      if (url === blobUrl) {
        storageKey = key;
        break;
      }
    }

    let blob: Blob | null = null;
    if (storageKey) {
      const db = await openDB();
      const key = storageKey;
      blob = await new Promise<Blob | null>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const request = tx.objectStore(STORE_NAME).get(key);
        request.onsuccess = () => resolve((request.result?.blob as Blob | undefined) ?? null);
        request.onerror = () => reject(request.error);
      });
    } else {
      // 缓存未命中时直接 fetch blob URL（同一会话内创建的 blob URL 仍有效）
      const resp = await fetch(blobUrl);
      blob = await resp.blob();
    }

    if (!blob) return null;
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * 批量把 URL 列表中的 blob: URL 解析为 data URL，其他 URL 原样保留。
 * 解析失败的条目被过滤（返回顺序与原列表一致）。
 */
export async function resolveBlobUrlsForApi(urls: string[]): Promise<string[]> {
  const result: string[] = [];
  for (const url of urls) {
    if (!url || !url.startsWith('blob:')) {
      if (url) result.push(url);
      continue;
    }
    const dataUrl = await blobUrlToDataUrl(url);
    if (dataUrl) {
      result.push(dataUrl);
    } else {
      console.warn('[canvas] 本地图片解析失败，已从参考图中移除:', url);
    }
  }
  return result;
}
